'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { DownloadIcon, PlusIcon, MinusIcon, PackageIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface GridSplitToolProps {
  images: string[];
}

export function GridSplitTool({ images }: GridSplitToolProps) {
  const [rows, setRows] = useState(2);
  const [cols, setCols] = useState(2);
  const [padding, setPadding] = useState([0]);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [outputFormat, setOutputFormat] = useState<'png' | 'jpeg' | 'webp'>('png');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  const containerRef = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState(1);

  const currentImage = images[currentIndex];

  // 加载图片获取尺寸
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      setImageLoaded(true);
    };
    img.onerror = () => {
      setImageLoaded(false);
    };
    img.src = currentImage;
  }, [currentImage]);

  // 计算预览缩放比例
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current || imageSize.width === 0) return;
      
      const containerWidth = containerRef.current.clientWidth - 32;
      const maxHeight = 400;
      
      const scaleX = containerWidth / imageSize.width;
      const scaleY = maxHeight / imageSize.height;
      const scale = Math.min(scaleX, scaleY, 1);
      
      setPreviewScale(scale);
    };
    
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [imageSize]);

  const totalCells = rows * cols;

  // 获取预览元素
  const renderPreview = () => {
    if (!imageLoaded || imageSize.width === 0) {
      return <div className="text-muted-foreground">加载中...</div>;
    }

    const previewWidth = imageSize.width * previewScale;
    const previewHeight = imageSize.height * previewScale;
    const gap = padding[0] * previewScale;

    return (
      <div className="relative overflow-hidden rounded-lg" style={{ width: previewWidth, height: previewHeight }}>
        {/* 背景 */}
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: bgColor }}
        />
        
        {/* 原始图片 */}
        <img
          src={currentImage}
          alt="预览"
          className="absolute inset-0 w-full h-full object-contain"
          crossOrigin="anonymous"
        />
        
        {/* 网格线 - 使用间距作为边框 */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.8) ${gap}px, transparent ${gap}px),
              linear-gradient(to bottom, rgba(255,255,255,0.8) ${gap}px, transparent ${gap}px)
            `,
            backgroundSize: `${previewWidth / cols}px ${previewHeight / rows}px`,
          }}
        />
        
        {/* 边框 */}
        <div 
          className="absolute inset-0 border-2 border-white"
          style={{ boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.3)' }}
        />
        
        {/* 编号 */}
        {Array.from({ length: totalCells }).map((_, index) => {
          const row = Math.floor(index / cols);
          const col = index % cols;
          const cellWidth = previewWidth / cols;
          const cellHeight = previewHeight / rows;
          const x = col * cellWidth + cellWidth / 2;
          const y = row * cellHeight + cellHeight / 2;
          
          return (
            <div
              key={index}
              className="absolute flex items-center justify-center"
              style={{
                width: cellWidth - gap,
                height: cellHeight - gap,
                left: col * cellWidth + gap / 2,
                top: row * cellHeight + gap / 2,
              }}
            >
              <div className="w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center text-sm font-bold">
                {index + 1}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 分割并下载
  const handleSplit = useCallback(async () => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = currentImage;
    });

    const cellWidth = img.naturalWidth / cols;
    const cellHeight = img.naturalHeight / rows;
    
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        
        // 输出尺寸 = 原始尺寸 + 间距
        const outputCellWidth = cellWidth + padding[0];
        const outputCellHeight = cellHeight + padding[0];
        
        canvas.width = outputCellWidth;
        canvas.height = outputCellHeight;
        
        // 绘制背景
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // 绘制图片（缩放以适应带间距的尺寸）
        const srcX = c * cellWidth;
        const srcY = r * cellHeight;
        
        // 如果有间距，需要裁剪更多内容
        if (padding[0] > 0) {
          // 计算实际需要绘制的区域，考虑间距带来的额外空间
          const scaleX = (cellWidth + padding[0]) / cellWidth;
          const scaleY = (cellHeight + padding[0]) / cellHeight;
          
          ctx.drawImage(
            img,
            srcX - (padding[0] / 2),
            srcY - (padding[0] / 2),
            cellWidth,
            cellHeight,
            0,
            0,
            outputCellWidth,
            outputCellHeight
          );
        } else {
          ctx.drawImage(
            img,
            srcX,
            srcY,
            cellWidth,
            cellHeight,
            0,
            0,
            cellWidth,
            cellHeight
          );
        }
        
        // 转换为 blob
        const format = outputFormat === 'jpeg' ? 'image/jpeg' : outputFormat === 'webp' ? 'image/webp' : 'image/png';
        const blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(resolve, format, 0.92);
        });
        
        if (blob) {
          const filename = `split-${r + 1}-${c + 1}.${outputFormat}`;
          zip.file(filename, blob);
        }
      }
    }

    // 生成 ZIP 并下载
    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = `grid-split-${rows}x${cols}-${Date.now()}.zip`;
    link.click();
    URL.revokeObjectURL(url);
  }, [currentImage, rows, cols, padding, bgColor, outputFormat]);

  return (
    <div className="space-y-6">
      {/* 工具栏 */}
      <div className="flex flex-wrap items-center gap-6 rounded-lg border bg-muted/50 p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">行数</Label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setRows(Math.max(1, rows - 1))}
              >
                <MinusIcon className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                value={rows}
                onChange={(e) => setRows(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                className="w-16 text-center"
                min={1}
                max={20}
              />
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setRows(Math.min(20, rows + 1))}
              >
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">列数</Label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCols(Math.max(1, cols - 1))}
              >
                <MinusIcon className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                value={cols}
                onChange={(e) => setCols(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                className="w-16 text-center"
                min={1}
                max={20}
              />
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCols(Math.min(20, cols + 1))}
              >
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">间距</Label>
            <Slider
              value={padding}
              onValueChange={setPadding}
              max={50}
              min={0}
              step={1}
              className="w-32"
            />
            <span className="w-12 text-sm">{padding[0]}px</span>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">背景</Label>
            <Input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="h-8 w-12 cursor-pointer"
            />
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">格式</Label>
            <Select value={outputFormat} onValueChange={(val) => setOutputFormat(val as 'png' | 'jpeg' | 'webp')}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="png">PNG</SelectItem>
                <SelectItem value="jpeg">JPEG</SelectItem>
                <SelectItem value="webp">WebP</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* 预览和结果 */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* 预览 */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">预览 ({rows} x {cols} = {totalCells} 张)</h3>
            {imageSize.width > 0 && (
              <span className="text-sm text-muted-foreground">
                原始尺寸: {imageSize.width} x {imageSize.height} px
              </span>
            )}
          </div>
          
          {/* 图片预览 */}
          <div 
            ref={containerRef}
            className="flex items-center justify-center rounded-lg border bg-muted p-4 overflow-auto"
            style={{ minHeight: '400px' }}
          >
            <div className="flex items-center justify-center">
              {renderPreview()}
            </div>
          </div>
          
          {/* 图片切换 */}
          {images.length > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
              >
                上一张
              </Button>
              <span className="text-sm">
                {currentIndex + 1} / {images.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentIndex(Math.min(images.length - 1, currentIndex + 1))}
                disabled={currentIndex === images.length - 1}
              >
                下一张
              </Button>
            </div>
          )}
          
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">输出信息</span>
              <span>每张子图: {Math.round(imageSize.width / cols + padding[0])} x {Math.round(imageSize.height / rows + padding[0])} px</span>
            </div>
          </div>
        </div>

        {/* 操作 */}
        <div className="space-y-4">
          <h3 className="font-medium">设置说明</h3>
          <div className="rounded-lg border bg-card p-4">
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• 行数和列数决定分割成多少张小图</li>
              <li>• 间距为导出的子图之间的留白（像素单位）</li>
              <li>• 背景色用于填充留白区域</li>
              <li>• 点击下载将打包成 ZIP 文件下载</li>
              <li>• 每张子图会自动编号方便识别</li>
            </ul>
          </div>

          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">{totalCells}</div>
              <div className="text-sm text-muted-foreground">将生成 {totalCells} 张子图</div>
            </div>
          </div>

          <Button className="w-full" size="lg" onClick={handleSplit}>
            <PackageIcon className="mr-2 h-4 w-4" />
            下载 ZIP 压缩包 ({totalCells} 张)
          </Button>
        </div>
      </div>
    </div>
  );
}
